/**
 * Music + ambient bed module for Survey Says. HTMLAudioElement streaming
 * routed through the shared AudioContext via createMediaElementSource so the
 * music gain still applies. Mirrors WhatIs audio/music.ts.
 *
 * Beds:
 *   lobby   — looping in lobby scene
 *   round   — looping low-intensity bed during in-game scenes (round-bed.mp3)
 *   results — short cue under podium / game-over
 */

import lobbyBedUrl from '../assets/music/lobby-bed.mp3?url';
import roundBedUrl from '../assets/music/round-bed.mp3?url';
import resultsBedUrl from '../assets/music/results-bed.mp3?url';

import { audioBus } from './bus.js';

export type MusicName = 'lobby' | 'round' | 'results';

interface MusicSpec { url: string; loop: boolean; gain: number }

const SPEC_LOBBY: MusicSpec = { url: lobbyBedUrl, loop: true, gain: 1.0 };
const SPEC_RESULTS: MusicSpec = { url: resultsBedUrl, loop: false, gain: 1.0 };
// Dedicated low-intensity round bed; loops under active gameplay.
const SPEC_ROUND: MusicSpec = { url: roundBedUrl, loop: true, gain: 1.0 };

function specOf(name: MusicName): MusicSpec {
  if (name === 'lobby') return SPEC_LOBBY;
  if (name === 'round') return SPEC_ROUND;
  return SPEC_RESULTS;
}

interface Track {
  el: HTMLAudioElement;
  source: MediaElementAudioSourceNode | null;
  trackGain: GainNode | null;
}

const tracks = new Map<MusicName, Track>();
let current: { name: MusicName; track: Track } | null = null;

function getTrack(name: MusicName): Track {
  let t = tracks.get(name);
  if (t) return t;
  const spec = specOf(name);
  // Each track owns its own HTMLAudioElement; createMediaElementSource is
  // one-per-element, so lobby + round (sharing lobby-bed.mp3 during fallback)
  // need separate Audio elements for independent playheads.
  const el = new Audio(spec.url);
  el.preload = 'auto';
  el.loop = spec.loop;
  let source: MediaElementAudioSourceNode | null = null;
  let trackGain: GainNode | null = null;
  const ctx = audioBus.context();
  if (ctx) {
    try {
      source = ctx.createMediaElementSource(el);
      // Per-track gain so the round-bed fallback ducks below the lobby bed
      // without changing the bus music gain.
      trackGain = ctx.createGain();
      trackGain.gain.value = spec.gain;
      source.connect(trackGain);
      trackGain.connect(audioBus.musicGain());
    } catch (err) {
      console.warn(`[ss-music] media-element-source failed for ${name}:`, err);
    }
  }
  t = { el, source, trackGain };
  tracks.set(name, t);
  return t;
}

export const music = {
  play(name: MusicName): void {
    if (current && current.name === name && !current.track.el.paused) return;
    if (current) {
      try { current.track.el.pause(); } catch { /* ignore */ }
    }
    const track = getTrack(name);
    current = { name, track };
    if (!audioBus.isUnlocked()) {
      void audioBus.unlock();
    }
    try {
      track.el.currentTime = 0;
      const p = track.el.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          console.debug(`[ss-music] autoplay blocked for ${name}:`, err);
        });
      }
    } catch (err) {
      console.warn(`[ss-music] play ${name} failed:`, err);
    }
  },
  stop(): void {
    if (!current) return;
    try { current.track.el.pause(); } catch { /* ignore */ }
    current = null;
  },
  resumeIfPending(): void {
    if (!current) return;
    const el = current.track.el;
    if (el.paused) {
      const p = el.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { /* still blocked, ignore */ });
      }
    }
  },
};
